import { useEffect, useMemo, useState } from "react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import api, { formatApiError } from "@/lib/api";
import Gauge from "@/components/Gauge";
import {
  KPI_FIELDS, MONTHS, YEARS,
  fmtNum, periodLabel, objMult,
} from "@/lib/kpi";

const CUR_Y = 2026;

export default function TeamPerformance() {
  const [view, setView] = useState("mensuel");
  const [selYear, setSelYear] = useState(CUR_Y);
  const [selMonth, setSelMonth] = useState(new Date().getMonth());
  const [selQ, setSelQ] = useState(0);

  const [monthly, setMonthly] = useState([]); // [{year,month,mandats,...}]
  const [objs, setObjs] = useState(Object.fromEntries(KPI_FIELDS.map((f) => [f.key, 0])));
  const [loading, setLoading] = useState(true);

  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState("");
  const [aiErr, setAiErr] = useState("");

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const [kpiRes, objRes] = await Promise.all([
          api.get(`/team/kpis?year=${selYear}`),
          api.get("/team/objectives"),
        ]);
        if (cancel) return;
        setMonthly(kpiRes.data || []);
        const o = {};
        KPI_FIELDS.forEach((f) => {
          o[f.key] = Number(objRes.data[f.key]) || 0;
        });
        setObjs(o);
      } catch (e) {
        console.error("Team load failed", e);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [selYear]);

  const aggregated = useMemo(() => {
    const acc = Object.fromEntries(KPI_FIELDS.map((f) => [f.key, 0]));
    let monthsIdx = [];
    if (view === "mensuel") monthsIdx = [selMonth];
    else if (view === "trimestriel") {
      const b = selQ * 3;
      monthsIdx = [b, b + 1, b + 2];
    } else {
      monthsIdx = [...Array(12).keys()];
    }
    monthsIdx.forEach((m) => {
      const r = monthly.find((x) => x.month === m);
      if (!r) return;
      KPI_FIELDS.forEach((f) => {
        acc[f.key] += Number(r[f.key]) || 0;
      });
    });
    return acc;
  }, [monthly, view, selMonth, selQ]);

  const chartData = useMemo(() => {
    if (view === "trimestriel") {
      return [0, 1, 2, 3].map((q) => {
        let mandats = 0, compromis = 0, actes = 0, ca = 0;
        for (const i of [q * 3, q * 3 + 1, q * 3 + 2]) {
          const r = monthly.find((x) => x.month === i);
          if (!r) continue;
          mandats += Number(r.mandats) || 0;
          compromis += Number(r.compromis) || 0;
          actes += Number(r.actes) || 0;
          ca += Number(r.ca) || 0;
        }
        return { name: `T${q + 1}`, mandats, compromis, actes, ca };
      });
    }
    // For mensuel and annuel, show 12 months
    return MONTHS.map((m, i) => {
      const r = monthly.find((x) => x.month === i) || {};
      return {
        name: m,
        mandats: Number(r.mandats) || 0,
        compromis: Number(r.compromis) || 0,
        actes: Number(r.actes) || 0,
        ca: Number(r.ca) || 0,
      };
    });
  }, [view, monthly]);

  const mult = objMult(view);

  const runAI = async () => {
    setAiErr("");
    setAiResult("");
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/analyze-team", {
        view,
        year: selYear,
        month: selMonth,
        quarter: selQ,
      });
      setAiResult(data.text || "");
    } catch (e) {
      setAiErr(formatApiError(e));
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div data-testid="team-performance" style={{ marginBottom: 28 }}>
      <div className="sec-title" style={{ fontSize: 14, marginBottom: 14 }}>
        Performance équipe
      </div>

      {/* Pills */}
      <div className="pills">
        {["mensuel", "trimestriel", "annuel"].map((v) => (
          <button
            key={v}
            className={`pill ${view === v ? "pill-active" : "pill-inactive"}`}
            onClick={() => { setView(v); setAiResult(""); }}
            data-testid={`team-view-pill-${v}`}
          >
            {v === "mensuel" ? "Mensuel" : v === "trimestriel" ? "Trimestriel" : "Annuel"}
          </button>
        ))}
      </div>

      {/* Period selectors */}
      <div className="period-bar">
        <select
          className="period-sel"
          value={selYear}
          onChange={(e) => setSelYear(Number(e.target.value))}
          data-testid="team-year-select"
        >
          {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        {view === "mensuel" && (
          <select
            className="period-sel"
            value={selMonth}
            onChange={(e) => { setSelMonth(Number(e.target.value)); setAiResult(""); }}
            data-testid="team-month-select"
          >
            {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
          </select>
        )}
        {view === "trimestriel" && (
          <select
            className="period-sel"
            value={selQ}
            onChange={(e) => { setSelQ(Number(e.target.value)); setAiResult(""); }}
            data-testid="team-quarter-select"
          >
            {[0, 1, 2, 3].map((q) => <option key={q} value={q}>T{q + 1}</option>)}
          </select>
        )}
        <div className="period-lbl">
          Période : <span>{periodLabel(view, selYear, selMonth, selQ)}</span>
        </div>
      </div>

      {/* KPI gauges */}
      <div className="grid-kpi">
        {KPI_FIELDS.map((f) => {
          const display = Number(aggregated[f.key]) || 0;
          const o = (Number(objs[f.key]) || 0) * mult;
          return (
            <div
              key={f.key}
              className="card kpi-row"
              data-testid={`team-kpi-${f.key}`}
            >
              <Gauge value={display} objective={o} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="kpi-label">{f.label}</div>
                <div className="kpi-val">
                  {loading ? "—" : fmtNum(display, f.unit)}
                </div>
                <div className="kpi-obj">Obj. {fmtNum(o, f.unit)}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Activity chart */}
      <div className="card card-pad" style={{ marginBottom: 24 }}>
        <div className="sec-title">Évolution équipe — Activité</div>
        <div className="chart-wrap">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#888" }} />
              <YAxis tick={{ fontSize: 11, fill: "#888" }} />
              <Tooltip
                contentStyle={{
                  background: "#fff",
                  border: "1px solid #eee",
                  borderRadius: 6,
                  fontSize: 12,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="mandats" name="Mandats" fill="#c0392b" radius={[3, 3, 0, 0]} />
              <Bar dataKey="compromis" name="Compromis" fill="#2c3e50" radius={[3, 3, 0, 0]} />
              <Bar dataKey="actes" name="Actes" fill="#7f8c8d" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* CA chart */}
      <div className="card card-pad" style={{ marginBottom: 24 }}>
        <div className="sec-title">Évolution équipe — CA HT (€)</div>
        <div className="chart-wrap-sm">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#888" }} />
              <YAxis
                tick={{ fontSize: 11, fill: "#888" }}
                tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
              />
              <Tooltip
                formatter={(v) => `${Number(v).toLocaleString("fr-FR")} €`}
                contentStyle={{
                  background: "#fff",
                  border: "1px solid #eee",
                  borderRadius: 6,
                  fontSize: 12,
                }}
              />
              <Line
                type="monotone"
                dataKey="ca"
                name="CA HT"
                stroke="#c0392b"
                strokeWidth={2.5}
                dot={{ r: 3, fill: "#c0392b" }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* AI team analysis */}
      <div className="card card-pad">
        <div className="ai-header">
          <div className="sec-title" style={{ marginBottom: 0 }}>
            Analyse IA — Équipe
          </div>
          <button
            className="btn btn-red"
            onClick={runAI}
            disabled={aiLoading}
            data-testid="team-run-ai-btn"
          >
            {aiLoading ? "Analyse en cours…" : "Générer l'analyse équipe"}
          </button>
        </div>
        {aiErr && (
          <div className="ai-result" style={{ color: "#c0392b" }}>
            {aiErr}
          </div>
        )}
        {aiResult && (
          <div className="ai-result" data-testid="team-ai-result">
            {aiResult}
          </div>
        )}
      </div>
    </div>
  );
}
